import type { IWarehouseConfigService } from './IWarehouseConfigService';
import type { IProductService } from './product/IProductService';
import type { ISparePartService } from './spare-part/ISparePartService';
import type { IWarehouseService } from './warehouse/IWarehouseService';
import type { IDocumentService } from './document/IDocumentService';
import type { IStockService } from './stock/IStockService';
import type { ISupplierService } from './supplier/ISupplierService';
import type { IProductInventoryService } from './productInventory/IProductInventoryService';
import type { IImportTemplateService } from './import-template/IImportTemplateService';

import { WarehouseConfigService } from './WarehouseConfigService';
import { ProductService } from './product/ProductService';
import { SparePartService } from './spare-part/SparePartService';
import { WarehouseService } from './warehouse/WarehouseService';
import { DocumentService } from './document/DocumentService';
import { StockService } from './stock/StockService';
import { SupplierService } from './supplier/SupplierService';
import { ProductInventoryService } from './productInventory/ProductInventoryService';
import { ImportTemplateService } from './import-template/ImportTemplateService';

import type { IComponentService } from './component/IComponentService';
import { ComponentService } from './component/ComponentService';
import type { IProductComponentsService } from './product/IProductComponentsService';
import { ProductComponentsService } from './product/ProductComponentsService';
import type { IProductImportService } from './product-import/IProductImportService';
import { ProductImportService } from './product-import/ProductImportService';

// ── Singleton instances ───────────────────────────────────────────────────────

export const warehouseConfigService: IWarehouseConfigService =
    new WarehouseConfigService();

export const productService: IProductService =
    new ProductService();

export const sparePartService: ISparePartService =
    new SparePartService();

export const warehouseService: IWarehouseService =
    new WarehouseService();

export const documentService: IDocumentService =
    new DocumentService();

export const stockService: IStockService =
    new StockService();

export const supplierService: ISupplierService =
    new SupplierService();

export const productInventoryService: IProductInventoryService =
    new ProductInventoryService();

export const importTemplateService: IImportTemplateService =
    new ImportTemplateService();

export const componentService: IComponentService = new ComponentService();

export const productComponentsService: IProductComponentsService =
    new ProductComponentsService();

export const productImportService: IProductImportService =
    new ProductImportService();

// ── Interfaces ────────────────────────────────────────────────────────────────

export type { IWarehouseConfigService } from './IWarehouseConfigService';
export type { IProductService } from './product/IProductService';
export type { ISparePartService } from './spare-part/ISparePartService';
export type { IWarehouseService } from './warehouse/IWarehouseService';
export type { IDocumentService } from './document/IDocumentService';
export type { IStockService } from './stock/IStockService';
export type { ISupplierService } from './supplier/ISupplierService';
export type { IProductInventoryService } from './productInventory/IProductInventoryService';
export type { IImportTemplateService } from './import-template/IImportTemplateService';
export type { IComponentService } from './component/IComponentService';
export type { IProductComponentsService } from './product/IProductComponentsService';
export type { IProductImportService } from './product-import/IProductImportService';

// ── Services ──────────────────────────────────────────────────────────────────

export { WarehouseConfigService } from './WarehouseConfigService';
export { ProductService } from './product/ProductService';
export { SparePartService } from './spare-part/SparePartService';
export { WarehouseService } from './warehouse/WarehouseService';
export { DocumentService } from './document/DocumentService';
export { StockService } from './stock/StockService';
export { SupplierService } from './supplier/SupplierService';
export { ProductInventoryService } from './productInventory/ProductInventoryService';
export { ProductInventoryEndpoints } from './productInventory/ProductInventoryEndpoints';
export { ImportTemplateService } from './import-template/ImportTemplateService';
export { ComponentService } from './component/ComponentService';
export { ProductComponentsService } from './product/ProductComponentsService';
export { ProductComponentsEndpoints } from './product/ProductComponentsEndpoints';

// ── HTTP helpers ──────────────────────────────────────────────────────────────

export { QueryBuilder } from './http/QueryBuilder';
export { JsonRequestBody } from './http/JsonRequestBody';
export { ListParams } from './http/ListParams';
export { StockListParams } from './http/StockListParams';
export { DataTableCriteriaQueryMapper } from './http/DataTableCriteriaQueryMapper';

// ── Configuration DTOs ────────────────────────────────────────────────────────

export type { ApiConfiguration } from './dto/ApiConfiguration.dto';
export type { ApiUpdateConfigurationRequest } from './dto/ApiUpdateConfigurationRequest.dto';
export type { ApiProductParameters } from './dto/ApiProductParameters.dto';
export type { ApiStockDocumentParameters } from './dto/ApiStockDocumentParameters.dto';
export type { ApiInventoryParameters } from './dto/ApiInventoryParameters.dto';
export type { DocumentNumberingRule } from './dto/DocumentNumberingRule.dto';
export type { CreateImportTemplateRequest } from './dto/CreateImportTemplateRequest';
export type { UpdateImportTemplateRequest } from './dto/UpdateImportTemplateRequest';

// ── Domain DTOs ───────────────────────────────────────────────────────────────

export type { StockLevel } from './dto/StockLevel.dto';
export type { PagedResponse } from './dto/PagedResponse';
export type { Product } from './dto/Product.dto';
export type { CreateProductRequest } from './dto/CreateProductRequest';
export type { UpdateProductRequest } from './dto/UpdateProductRequest';
export type { SparePart } from './dto/SparePart.dto';
export type { CreateSparePartRequest } from './dto/CreateSparePartRequest';
export type { UpdateSparePartRequest } from './dto/UpdateSparePartRequest';
export type { Warehouse } from './dto/Warehouse.dto';
export type { CreateWarehouseRequest } from './dto/CreateWarehouseRequest';
export type { UpdateWarehouseRequest } from './dto/UpdateWarehouseRequest';
export type { WarehouseDocument } from './dto/Document.dto';
export type { WarehouseDocumentDetail } from './dto/WarehouseDocumentDetail';
export type { DocumentLineItem } from './dto/DocumentLineItem';
export type { CreateDocumentRequest } from './dto/CreateDocumentRequest';
export type { UpdateDocumentRequest } from './dto/UpdateDocumentRequest';
export type { CreateLineItemRequest } from './dto/CreateLineItemRequest';
export type { UpdateLineItemRequest } from './dto/UpdateLineItemRequest';
export type { Supplier } from './dto/Supplier.dto';
export type { CreateSupplierRequest } from './dto/CreateSupplierRequest';
export type { UpdateSupplierRequest } from './dto/UpdateSupplierRequest';
export type { ProductInventoryItemDto } from './dto/ProductInventoryItem.dto';
export { ProductInventoryListParams } from './dto/ProductInventoryListParams';
export type { ImportTemplate } from './dto/ImportTemplate.dto';
export type { ProductComponentDto } from './dto/ProductComponent.dto';
export type { SetProductComponentRequest } from './dto/SetProductComponentRequest';
export type { ProductImportRow } from './dto/ProductImportRow.dto';
export type { ProductImportResult } from './dto/ProductImportResult';

export type {
    ComponentDto,
} from './dto/Component.dto';

export type { CreateComponentRequest } from './dto/CreateComponentRequest';
export type { UpdateComponentRequest } from './dto/UpdateComponentRequest';
export type { UpdateComponentStatusRequest } from './dto/UpdateComponentStatusRequest';
export type { ComponentStockLevelDto } from './dto/ComponentStockLevel.dto';

// ── Models ────────────────────────────────────────────────────────────────────

export type { ProductInventoryOption } from './model/ProductInventoryOption';

// ── Mappers ───────────────────────────────────────────────────────────────────

export type { IProductInventoryOptionMapper } from './mapper/IProductInventoryOptionMapper';
export type { IProductInventoryResponseMapper } from './mapper/IProductInventoryResponseMapper';

export { ProductInventoryOptionMapper } from './mapper/ProductInventoryOptionMapper';
export { ProductInventoryResponseMapper } from './mapper/ProductInventoryResponseMapper';

// ── Types ─────────────────────────────────────────────────────────────────────

export type { ReplenishmentMode } from './types/ReplenishmentMode.type';
export type { ConfigurationNumberingMode } from './types/ConfigurationNumberingMode.type';
export type { ResourceStatus } from './types/ResourceStatus.type';
export type { ResourceType } from './types/ResourceType.type';
export type { WarehouseType } from './types/WarehouseType.type';
export type { WarehouseStatus } from './types/WarehouseStatus.type';
export type { DocumentType } from './types/DocumentType.type';
export type { DocumentStatus } from './types/DocumentStatus.type';
export type { SupplierStatus } from './types/SupplierStatus.type';
export type { ComponentStatus } from './types/ComponentStatus.type';
export type { ComponentUom } from './types/ComponentUom.type';